import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { StatusChip } from '../../design/primitives';
import { stromaCompactPanel } from './askPresentation';

const STAGE_LABELS: Record<string, string> = {
  analysis_started: 'Starting',
  analysis_progress: 'Querying',
  mission_planned: 'Planning',
  mission_started: 'Planning',
  sub_task_started: 'Working',
  worker_result: 'Querying',
  gatekeeper_verdict: 'Reviewing',
  retry_queued: 'Retrying',
  formatter_result: 'Formatting',
  synthesis_started: 'Synthesizing',
  synthesis_result: 'Synthesizing',
  clarification_required: 'Needs input',
};

const TERMINAL_STATUS: Record<string, { status: 'done' | 'draft' | 'stop' | 'off'; label: string }> = {
  clarifying: { status: 'draft', label: 'Needs input' },
  defining: { status: 'draft', label: 'Needs definition' },
  done: { status: 'done', label: 'Done' },
  error: { status: 'stop', label: 'Failed' },
  cancelled: { status: 'off', label: 'Stopped' },
  idle: { status: 'off', label: 'Idle' },
};

function currentStage(events: any[]): { label: string; detail?: string } | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    const label = STAGE_LABELS[e.type];
    if (!label) continue;
    let detail: string | undefined;
    if (e.type === 'sub_task_started') {
      detail = e.worker_type === 'query_worker' ? 'querying data'
        : e.worker_type === 'result_formatter' ? 'formatting results'
        : e.description?.slice(0, 60);
    } else if (e.type === 'worker_result' && e.row_count != null) {
      detail = `${e.row_count} rows`;
    } else if (e.type === 'gatekeeper_verdict') {
      detail = e.verdict;
    } else if (e.type === 'retry_queued') {
      detail = e.correction_category;
    }
    return { label, detail };
  }
  return null;
}

function describe(evt: any): string | null {
  const t = evt?.type;
  switch (t) {
    case 'analysis_started': return 'Background analysis started';
    case 'analysis_progress': return evt.description || 'Querying data';
    case 'mission_planned':
      return `Planned ${(evt.sub_tasks || []).length} sub-task(s) · mode=${evt.mode}`;
    case 'mission_started':
      return `Mission started (${evt.sub_task_count} sub-tasks)`;
    case 'sub_task_started':
      return `[${evt.worker_type}] ${evt.sub_task_id}${evt.attempt && evt.attempt > 1 ? ` (retry ${evt.attempt})` : ''}: ${evt.description || ''}`;
    case 'worker_result':
      return `Worker → ${evt.status}${evt.row_count != null ? ` · ${evt.row_count} rows` : ''}${evt.execution_time_ms != null ? ` · ${evt.execution_time_ms}ms` : ''}`;
    case 'gatekeeper_verdict':
      return `Gatekeeper: ${evt.verdict}${evt.confidence != null ? ` (${evt.confidence})` : ''}`;
    case 'retry_queued':
      return `Retry queued: ${evt.correction_category || ''}`;
    case 'retry_exhausted':
      return `Max retries reached`;
    case 'formatter_result':
      return `Formatter → ${evt.status}`;
    case 'synthesis_started':
      return `Synthesizing across ${evt.data_producing_tasks} result(s)`;
    case 'synthesis_result':
      return `Synthesis complete`;
    case 'synthesis_skipped':
      return `Synthesis skipped`;
    case 'clarification_required':
      return `Clarification needed`;
    case 'sub_task_skipped':
      return `Skipped ${evt.sub_task_id}: ${evt.reason}`;
    case 'sub_task_error':
      return `Error in ${evt.sub_task_id}: ${evt.error}`;
    case 'mission_error':
      return `Mission error: ${evt.error}`;
    case 'mission_complete':
      return null;
    default:
      return null;
  }
}

function iconFor(evt: any, running: boolean) {
  const t = evt?.type;
  if (t === 'sub_task_error' || t === 'mission_error' || t === 'retry_exhausted') {
    return <ErrorIcon fontSize="small" color="error" />;
  }
  if (t === 'clarification_required') {
    return <HelpIcon fontSize="small" color="warning" />;
  }
  if (t === 'worker_result' || t === 'formatter_result' || t === 'synthesis_result') {
    return <CheckCircleIcon fontSize="small" color="success" />;
  }
  // Progress events: once the mission is no longer running, everything we see
  // in the log is a step that finished successfully → green check.
  if (!running) {
    return <CheckCircleIcon fontSize="small" color="success" />;
  }
  return <CircularProgress size={14} />;
}

export default function AgentStatusPanel(
  { turn, defaultCollapsed }: { turn?: LandingAgentTurn; defaultCollapsed?: boolean } = {},
) {
  const storeEvents = useVoyagerStore((s) => s.landingAgentEvents);
  const storeStatus = useVoyagerStore((s) => s.landingAgentStatus);
  const events = turn ? turn.events : storeEvents;
  const status = turn ? turn.status : storeStatus;
  const running = status === 'running';
  const [expanded, setExpanded] = React.useState(!defaultCollapsed);

  // Auto-collapse once the mission is no longer running.
  React.useEffect(() => {
    if (!running && status !== 'idle') setExpanded(false);
    if (running) setExpanded(true);
  }, [running, status]);

  if (status === 'idle') return null;

  if (events.length === 0) {
    // A mission that has finished and logged no activity has nothing to
    // report — showing a spinner beside the word "done" reads as stuck.
    // Reachable when replaying a stored answer, which carries the result but
    // not the event log.
    if (!running) return null;
    return (
      <Box
        sx={{
          p: 2,
          ...stromaCompactPanel,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CircularProgress size={14} />
        <Typography variant="body2" color="text.secondary">
          Thinking…
        </Typography>
      </Box>
    );
  }

  const rows = events
    .map((e) => ({ e, line: describe(e) }))
    .filter((r) => r.line);

  const stage = running ? currentStage(events) : null;
  const terminal = !running ? TERMINAL_STATUS[status] : null;

  return (
    <Box
      sx={{
        ...stromaCompactPanel,
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">
            Agent activity · {rows.length} step{rows.length === 1 ? '' : 's'}
          </Typography>
          {stage && (
            <StatusChip
              status="running"
              size="small"
              label={stage.detail ? `${stage.label} · ${stage.detail}` : stage.label}
              sx={{ height: 22, fontSize: 11 }}
            />
          )}
          {terminal && <StatusChip status={terminal.status} size="small" label={terminal.label} sx={{ height: 22, fontSize: 11 }} />}
        </Box>
        <IconButton size="small" sx={{ p: 0.25 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, maxHeight: 300, overflow: 'auto' }}>
          {rows.map((r, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
              {iconFor(r.e, running)}
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                {r.line}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
